import { HttpClient, HttpEvent, HttpEventType, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FileUploadDialogComponent } from 'src/app/shared/components/file-upload-dialog/file-upload-dialog.component';
import { FileUploadDialogConfig, SubmittedFile } from 'src/app/shared/components/file-upload-dialog/file-upload-dialog.types';
import { FileCategory } from './claim/claim.service';
import { AppDataService } from 'src/app/core/app-data/app-data.service';
import { ENROLLED_UNIT, ENROLLMENT_FILE, ENROLLMENT_FILES } from '../api.constants';
import { SerialNumberValidation } from './espp/application.types';
export type  FormattedDate = {[key: string]: string}
export type File = {
    id: number
    name: string,
    category: string | null
    url: string
    size?: number
}
export type Coverage = {
    id: number
    coverage_type: string
    coverage: string
    status: { name: string }
    term: number
    hours: number | 'Unlimited'
    effective_date: Date
    expiration_date: Date
    deductible: number | null
    retail_cost: number
    use_type: string
    comments: string
    currency: 'USD' | 'CAD' | 'NZD' | 'AUD'
    quote_type: string,
    date_of_sale: Date,
    purchase_order: string | null,
    formatted_dates: FormatedDate
}

type FormatedDate = {[key: string]: string}
export interface EnrolledUnit {
    id: number
    quote_number: string | null
    date_submitted: Date
    plan_number: string
    plan_sold_by: string | null
    plan_sold_by_id: string | null
    plan_type: string
    quote_type: string
    program?: string
    files: File[],
    invoice_code?: string,
    comments: string[],
    policy_number: string
    documents: any[],
    equipment: {
        year: number,
        manufacturer: string,
        equipment_type: string,
        model: string,
        new: boolean
        equipment_name: string
        serial_number: string | null,
        pin17: string | null
        manufacturer_ship_date: Date
        manufacturer_date: Date
        hours: number
        value: number
        mfr_warranty: string | null
        coverages: Coverage[],
        existing_term: string | null,
        formatted_dates: FormatedDate
        serial_number_validation?: SerialNumberValidation | null,

    }[]
    dealer: {id: number, dealer_corp_id:number} | null
    customer: {name: string} | null,
    model_type: string,
    formatted_dates: FormattedDate,
    financier: {[$key: string]: any},
    invoice: {invoice_number: string, invoice_date: Date,  formatted_dates: FormatedDate},
    viewing_entity: boolean,
    contract: Contract | null
    serial_number_validation?: SerialNumberValidation | null,
}

interface Contract {
    id: number;
    description: string;
    country: string | null;
    active: boolean;
    claim_requirememnts: {
        gst: boolean
    }
        
}

export interface EnrolledUnitQueueResponse {
    data: EnrolledUnit[]
    meta: {
        current_page: number
        last_page: number
        per_page: number
        total: number
    }
}

@Injectable()
export class EnrolledUnitService {

    public enrolledUnit = new BehaviorSubject<EnrolledUnit | null>(null)
    public isDetailsOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(private http: HttpClient, private modal: MatDialog, private appDataService: AppDataService) { }

    getEnrolledUnits(page: number = 1): Observable<EnrolledUnitQueueResponse> {
        return this.http.get<EnrolledUnitQueueResponse>(`api/v1/espp/enrolled_units?page=${page}`)
    }

    getEnrolledUnit(id: number, params: any): Observable<EnrolledUnit> {
        const qParams = params ?? {}
        return this.appDataService.get<EnrolledUnit>(ENROLLED_UNIT+id, qParams)
            .pipe(
                tap(enrolledUnit => {
                    this.enrolledUnit.next(enrolledUnit)
                })
            )
    }

    searchEnrolledUnits(term: string): Observable<EnrolledUnitQueueResponse>{
        let params = new HttpParams()
        params = params.append('term',term)
        return this.http.post<EnrolledUnitQueueResponse>(`api/v1/espp/enrolled_units/search`,{term})
    }

    public openFileModal(id: number): void {
        const existingFiles = (this.enrolledUnit.value?.files ?? []).filter(f => f.category !== 'TC' && f.category !== 'MPS');
        const config: FileUploadDialogConfig = {
            title: 'File Upload',
            footerNote: 'All supporting documentation, such as coverage documents, equipment purchase records, and other required documentation (when required), should be attached in order for the enrollment to be reviewed and processed.',
            existingFileCount: existingFiles.length,
            existingTotalBytes: existingFiles.reduce((sum, f) => sum + (f.size ?? 0), 0),
            domain: 'enrolled_units',
            fileableType: 'enrolled_units',
            fileableId: id,
        };
        this.modal.open(FileUploadDialogComponent, { data: config })
            .afterClosed()
            .subscribe((submittedFiles?: SubmittedFile[]) => {
                if (!submittedFiles?.length) return;
                const enrolledUnit = this.enrolledUnit.value;
                if (enrolledUnit?.id !== id) return;
                const newFiles: File[] = submittedFiles.map(f => ({
                    id: f.id,
                    name: f.name,
                    category: f.category,
                    url: f.url,
                    size: f.size,
                }));
                enrolledUnit.files = [...enrolledUnit.files, ...newFiles];
                this.enrolledUnit.next(enrolledUnit);
            });
    }

    public addFile(id: number, file: any, category: FileCategory): Observable<HttpEvent<File[]>> {
        const formData = new FormData();
        formData.append('file_category', category);
        formData.append('files[]', file);

        return this.http.post<File[]>(ENROLLMENT_FILES(id), formData, {
            params: new HttpParams(),
            reportProgress: true,
            observe: 'events' as const,
        }).pipe(
            tap(data => {
                if (data.type === HttpEventType.Response) {
                    const uploaded = data.body ?? [];
                    const enrolledUnit = this.enrolledUnit.value;
                    if (enrolledUnit?.id === id) {
                        enrolledUnit.files = [...enrolledUnit.files, ...uploaded];
                        this.enrolledUnit.next(enrolledUnit);
                    }
                }
            })
        );
    }

    public deleteFile(enrollmentId: number, fileId: number): Observable<void> {
        return this.http.delete<void>(ENROLLMENT_FILE(enrollmentId, fileId))
            .pipe(
                tap(() => {
                    const enrolledUnit = this.enrolledUnit.value;
                    if (enrolledUnit?.id === enrollmentId) {
                        enrolledUnit.files = enrolledUnit.files.filter(f => f.id !== fileId);
                        this.enrolledUnit.next(enrolledUnit);
                    }
                })
            );
    }


    public checkIfDetailsOpened(){
        let temp = false
        this.isDetailsOpen.subscribe( theData=>{
            temp = theData
        })
        return temp
    }

    public downloadPDF (url: string) {
        window.open(url, '_blank')
    }

}
